// useForge (§4.1, §5) — the shared combine engine for Free Forge and the Lab.
// Manages the two slots, the searchable tray, the 4 feedback states, combo
// praise, XP, achievements, streak, and the discovery celebration. Wires the
// existing store (discoverRecipe/unlock), soundService, progressionService and
// haptics — refactor, don't rewrite (§0).
import { useCallback, useMemo, useRef, useState } from "react";
import { useElementStore } from "../store/elementStore";
import { playSound } from "../services/soundService";
import { triggerHaptic } from "../services/capacitorService";
import { addXP, checkAchievements, Achievement } from "../services/progressionService";
import { touchStreak } from "./stats";
import { makePraise, Praise } from "./praise";

export type FeedbackKind = "info" | "warn" | "good";
export interface Feedback {
  text: string;
  kind: FeedbackKind;
}
export interface Discovery {
  id: string;
  ingredients: string[];
}

const COMBO_WINDOW_MS = 5000;

export interface ForgeModeOptions {
  /** Active mode; drives scoring and win-checks. Defaults to free play. */
  mode?: "free" | "time" | "daily" | "gauntlet" | "battle" | "lab";
  /** Target element id for daily/gauntlet/battle. */
  target?: string | null;
  /** Called with (gain, newTotal) when a discovery scores points (time mode). */
  onScore?: (gain: number, total: number) => void;
  /** Called with the result id when the mode target is reached. */
  onWin?: (resultId: string) => void;
}

export interface UseForge {
  slots: [string | null, string | null];
  query: string;
  setQuery: (q: string) => void;
  tray: string[];
  feedback: Feedback | null;
  praise: Praise | null;
  discovery: Discovery | null;
  achievement: Achievement | null;
  pickTray: (id: string) => void;
  placeInSlot: (id: string, index: 0 | 1) => void;
  clearSlot: (index: 0 | 1) => void;
  clearSlots: () => void;
  combine: () => void;
  closeDiscovery: () => void;
  clearAchievement: () => void;
  canCombine: boolean;
}

export function useForge(options: ForgeModeOptions = {}): UseForge {
  const { mode = "free", target = null, onScore, onWin } = options;
  const all = useElementStore((s) => s.all);
  const unlocked = useElementStore((s) => s.unlocked);
  const discoverRecipe = useElementStore((s) => s.discoverRecipe);
  const scoreRef = useRef(0);

  // keep the latest callbacks/target without re-creating combine each render
  const optsRef = useRef({ mode, target, onScore, onWin });
  optsRef.current = { mode, target, onScore, onWin };

  const [slots, setSlots] = useState<[string | null, string | null]>([null, null]);
  const [query, setQuery] = useState("");
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [praise, setPraise] = useState<Praise | null>(null);
  const [discovery, setDiscovery] = useState<Discovery | null>(null);
  const [achievement, setAchievement] = useState<Achievement | null>(null);

  const comboRef = useRef(0);
  const lastDiscRef = useRef(0);
  const praiseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Unlocked elements, searchable, most-recent first (tray).
  const tray = useMemo(() => {
    const ids = Array.from(unlocked).filter((id) => all[id]);
    const q = query.trim().toLowerCase();
    const filtered = q ? ids.filter((id) => all[id].name.toLowerCase().includes(q)) : ids;
    // stable order: base first, then by tier, then name
    return filtered.sort((a, b) => {
      const ta = all[a].tier - all[b].tier;
      return ta !== 0 ? ta : all[a].name.localeCompare(all[b].name);
    });
  }, [unlocked, all, query]);

  const placeInSlot = useCallback((id: string, index: 0 | 1) => {
    triggerHaptic("light");
    playSound("select");
    setFeedback(null);
    setSlots((s) => {
      const next: [string | null, string | null] = [s[0], s[1]];
      next[index] = id;
      return next;
    });
  }, []);

  const pickTray = useCallback((id: string) => {
    triggerHaptic("light");
    playSound("select");
    setFeedback(null);
    setSlots((s) => (!s[0] ? [id, s[1]] : [s[0], id]));
  }, []);

  const clearSlot = useCallback((index: 0 | 1) => {
    setFeedback(null);
    setSlots((s) => {
      const next: [string | null, string | null] = [s[0], s[1]];
      next[index] = null;
      return next;
    });
  }, []);

  const clearSlots = useCallback(() => {
    setFeedback(null);
    setSlots([null, null]);
  }, []);

  const closeDiscovery = useCallback(() => setDiscovery(null), []);
  const clearAchievement = useCallback(() => setAchievement(null), []);

  const combine = useCallback(() => {
    const [a, b] = slots;
    triggerHaptic("medium");
    playSound("combine");

    if (!a || !b) {
      setFeedback({ text: "Add two elements to the slots first.", kind: "info" });
      return;
    }

    // was this already discovered before we combine? (snapshot is pre-update)
    const result = discoverRecipe([a, b]);
    if (!result) {
      triggerHaptic("error");
      playSound("error");
      setFeedback({ text: "Nothing reacts — try a different pairing.", kind: "warn" });
      return;
    }

    const known = unlocked.has(result);
    const def = all[result];
    const { mode: m, target: t, onScore: score, onWin: win } = optsRef.current;

    const checkWin = () => {
      if ((m === "daily" || m === "gauntlet" || m === "battle") && t && result === t) {
        win?.(result);
      }
    };

    if (known) {
      setSlots([null, null]);
      setFeedback({ text: `You already know ${def.name} — nice pairing though!`, kind: "info" });
      checkWin();
      return;
    }

    // NEW discovery ---------------------------------------------------------
    triggerHaptic("success");
    playSound("discover");
    touchStreak();
    addXP(def.tier * 10);

    // combo + praise
    const now = Date.now();
    const combo = now - lastDiscRef.current < COMBO_WINDOW_MS ? comboRef.current + 1 : 1;
    comboRef.current = combo;
    lastDiscRef.current = now;
    const p = makePraise(combo, def.tier, now);
    setPraise(p);
    if (praiseTimer.current) clearTimeout(praiseTimer.current);
    praiseTimer.current = setTimeout(() => setPraise(null), 1500);

    setSlots([null, null]);

    // Time Attack scores instead of interrupting with the discovery card.
    if (m === "time") {
      const gain = def.tier * 10 * combo;
      scoreRef.current += gain;
      score?.(gain, scoreRef.current);
      setFeedback({ text: `Discovered ${def.name}! +${gain}`, kind: "good" });
    } else {
      setFeedback(null);
      setDiscovery({ id: result, ingredients: [a, b] });
    }

    // achievements computed from the now-updated store state
    const nowUnlocked = useElementStore.getState().unlocked;
    const earned = checkAchievements(nowUnlocked, 0);
    if (earned.length > 0) {
      setTimeout(() => setAchievement(earned[0]), 1800);
    }

    checkWin();
  }, [slots, discoverRecipe, unlocked, all]);

  return {
    slots,
    query,
    setQuery,
    tray,
    feedback,
    praise,
    discovery,
    achievement,
    pickTray,
    placeInSlot,
    clearSlot,
    clearSlots,
    combine,
    closeDiscovery,
    clearAchievement,
    canCombine: !!(slots[0] && slots[1]),
  };
}
